import Vue from "vue";
import Vuex from "vuex";
import axios from "@/plugins/axios";
import authJs from "@/plugins/auth";
import companiesJs from "@/plugins/companies";
import helpersJs from "@/plugins/helpers";
import autoServicesJs from "@/plugins/autoServices";
import invoicesAndQuotesJs from "@/plugins/invoicesAndQuotes";
import legalCasesJs from "@/plugins/legalCases";
import practicesJs from "@/plugins/practices";
import projectsJs from "@/plugins/projects";

Vue.use(Vuex);

const getLocalStorageData = (key) => {
  const item = localStorage.getItem(key);
  if (!item || item === "undefined") return null;

  try {
    return JSON.parse(item); // Parses JSON objects, arrays, booleans, numbers
  } catch (e) {
    return item; // If it fails (like a raw JWT string), return the plain string!
  }
};

export default new Vuex.Store({
  state: {
    appName: getLocalStorageData("appName") || "BitsInvoice",
    appMode: getLocalStorageData("o") || "general",
    trustAccLabel: getLocalStorageData("trustAcc") || "UpFront",
    apiUrl: "https://api.legalcasebilling.co.za",
    logo: getLocalStorageData("logo") || null,
    stamp: getLocalStorageData("stamp") || null,
    themeColor: getLocalStorageData("themeColor") || "#FFF", //"#014D4E",//"#0D2462" navy,//"#0D2024" baron,//"#282F32", //"#1976D2",
    secondaryColor: getLocalStorageData("secondaryColor") || "#FFF",
    screenWidth: null,
    industryCategory: getLocalStorageData("industryCategory") || null,
    selectedDirectoryCompany:
      getLocalStorageData("selectedDirectoryCompany") || null,
    currentUser: getLocalStorageData("currentUser"),
    accessToken: getLocalStorageData("token"),
    authenticated: getLocalStorageData("authenticated") || false,
    isa: getLocalStorageData("isa") || false,
    globalConfirmDelete: false,
    Loader: false,
    currency: getLocalStorageData("currency") || "R",
    allCases: getLocalStorageData("allCases") || [],
    allAutoServices: getLocalStorageData("allAutoServices") || [],
    allProjects: getLocalStorageData("allProjects") || [],
    allPractices: getLocalStorageData("allPractices") || [],
    eventIndustries: ["Event Planning", "Event Manager"],
    projectIndustries: [
      "Construction",
      "Freelance",
      "Project Manager",
      "Tender",
      "Home Services",
      "Logistics",
    ],
    privatePracticeIndustries: ["Private Practice", "General Practice"],
    socialWorkerIndustries: ["Social Worker"],
    modesMap: {
      legal: "Legal Case Billing",
      general: "BitsInvoice",
      autoservice: "Auto Services Invoicing",
      projects: "Projects Manager & Invoicing",
      events: "Events Manager & Invoicing",
      privatePractice: "Private Practice Invoicing",
      social: "Social Worker Invoicing",
    },
    trustAccMap: {
      legal: "Trust",
      general: "UpFront",
      autoservice: "UpFront",
      projects: "UpFront",
      events: "UpFront",
      privatePractice: "UpFront",
      social: "UpFront",
    },
    fileLabelMap: {
      legal: "Case",
      general: "Invoice",
      autoservice: "Job Card",
      projects: "Project",
      events: "Event",
      privatePractice: "Job File",
      social: "Job File",
    },
    clientLabelMap: {
      legal: "Client",
      general: "Client",
      autoservice: "Customer",
      projects: "Client",
      events: "Client",
      privatePractice: "Patient",
      social: "Client",
    },
    userCompany: getLocalStorageData("userCompany") || null,
    upgradeDialog: false,
  },
  getters: {
    isAdmin(state) {
      return state.isa;
    },
    getAppName(state) {
      return state.appName;
    },
    getLogo(state) {
      return state.logo;
    },
    getStamp(state) {
      return state.stamp;
    },
    getAppMode(state) {
      return state.appMode;
    },
    getFileLabel(state) {
      const mode = state.appMode;
      return state.fileLabelMap[mode];
    },
    getClientLabel(state) {
      const mode = state.appMode;
      return state.clientLabelMap[mode];
    },
    getIndustryCategory(state) {
      return state.industryCategory;
    },
    getSelectedDirectoryCompany(state) {
      return state.selectedDirectoryCompany;
    },
    getUpgradeDialog(state) {
      return state.upgradeDialog;
    },

    getAppModeFlags(state) {
      const mode = state.appMode;
      const knownModes = [
        "legal",
        "autoservice",
        "projects",
        "events",
        "pos",
        "store",
        "privatePractice",
        "social",
      ];

      return {
        itsLegal: mode === "legal",
        itsAuto: mode === "autoservice",
        itsProjects: mode === "projects",
        itsEvents: mode === "events",
        itsPOS: mode === "pos",
        itsStore: mode === "store",
        itsPractice: mode === "privatePractice",
        itsSocial: mode === "social",
        itsGeneral: !knownModes.includes(mode),
      };
    },
    getTrustAccLabel(state) {
      return state.trustAccLabel;
    },
    getApiUrl(state) {
      return state.apiUrl;
    },
    getScreenWidth(state) {
      return state.screenWidth;
    },
    isMobile(state, getters) {
      return getters.getScreenWidth && getters.getScreenWidth < 600;
    },
    getVat(state, getters) {
      const rawVat = Number(getters.getUserCompany?.vat_percentage);
      const isValidVat = !isNaN(rawVat) && isFinite(rawVat) && rawVat >= 0;

      const vat = isValidVat ? rawVat : 0;
      // Number() converts it back to a numeric type safely
      const calculatedValue = Number((vat / 100).toFixed(2));

      return {
        label: `${vat}%`,
        value: calculatedValue,
      };
    },
    getCurrency(state) {
      return state.currency;
    },
    getGlobalConfirmDelete(state) {
      return state.globalConfirmDelete;
    },
    getCurrentUser(state) {
      return state.currentUser;
    },
    getUserCompany(state) {
      return state.userCompany;
    },
    getAllCases(state) {
      return state.allCases;
    },
    getAllAutoServices(state) {
      return state.allAutoServices;
    },
    getAllProjects(state) {
      return state.allProjects;
    },
    getAllPractices(state) {
      return state.allPractices;
    },
    // 1. Method-style getter: returns a function accepting parameters
    adjustColor: () => (color, amount) => {
      // Example implementation adjusting hex color brightness
      let usePound = false;
      if (color[0] === "#") {
        color = color.slice(1);
        usePound = true;
      }
      let num = parseInt(color, 16);
      let r = (num >> 16) + amount;
      if (r > 255) r = 255;
      else if (r < 0) r = 0;
      let b = ((num >> 8) & 0x00ff) + amount;
      if (b > 255) b = 255;
      else if (b < 0) b = 0;
      let g = (num & 0x0000ff) + amount;
      if (g > 255) g = 255;
      else if (g < 0) g = 0;

      return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16);
    },
    gradientIconStyle(state, getters) {
      return {
        background: getters.getThemeColor,
        "-webkit-background-clip": "text",
        "-webkit-text-fill-color": "transparent",
        "background-clip": "text",
        display: "inline-block",
      };
    },

    // 2. Uses state and calls the adjustColor getter with 2 arguments
    getThemeColor: (state, getters) => {
      const color = state.themeColor;
      const darkerColor = "black";
      return `linear-gradient(173deg, ${color} 23%, ${darkerColor} 77%)`;
    },
    getPrimaryColor(state) {
      return state.themeColor;
    },

    getSecondaryColor(state) {
      return state.secondaryColor;
    },
    getAccessToken(state) {
      return state.accessToken;
    },
    getAuthenticated(state) {
      return state.authenticated;
    },
    getLoader(state) {
      return state.Loader;
    },
    getListingCategories() {
      return [
        {
          label: "Attorneys & Law Firms",
          icon: "mdi-scale-balance",
          slug: "attorneys",
          route: "attorneys",
          industries: ["Legal Practice", "Legal"],
          gradient: "linear-gradient(135deg, #332421 0%, #332119 100%)",
          description:
            "Find verified attorneys, advocates, and law firms sorted by expertise, province, and court jurisdiction.",
        },
        {
          label: "Doctors & General Practitioners",
          icon: "mdi-stethoscope",
          slug: "doctors-and-gps",
          route: "doctors-and-gps",
          industries: ["General Practice", "Private Practice"],
          gradient: "linear-gradient(135deg, #0B0B45 0%, #2a5298 100%)", //"linear-gradient(135deg, #0575E6 0%, #00F260 100%)",
          description:
            "Search medical doctors, private clinics, and healthcare specialists across South Africa.",
        },
        {
          label: "Auto Mechanics",
          icon: "mdi-wrench",
          slug: "auto-mechanics",
          route: "auto-mechanics",
          industries: ["Auto Services", "Auto Repairs", "Auto Mechanic"],
          gradient: "linear-gradient(135deg, #FF416C 0%, #FF4B2B 100%)",
          description:
            "Locate certified automotive repair workshops, diagnostics specialists, and mobile mechanics.",
        },
        {
          label: "Construction Businesses",
          icon: "mdi-hard-hat",
          slug: "construction-businesses",
          route: "construction-businesses",
          industries: ["Construction", "Tender", "Project Manager"],
          gradient: "linear-gradient(135deg, #F7971E 0%, #FFD200 100%)",
          description:
            "Discover general contractors, civil engineers, building suppliers, and renovation experts.",
        },
        {
          label: "Social Workers",
          icon: "mdi-account-heart",
          slug: "social-workers",
          route: "social-workers",
          industries: ["Social Worker"],
          gradient: "linear-gradient(135deg, #FF9966 0%, #4A00E0 100%)",
          description:
            "Connect with registered social practitioners, family counselors, and community care services.",
        },
        // {
        //   label: "Event Managers",
        //   icon: "mdi-party-popper",
        //   slug: "event-managers",
        //   route: "event-managers",
        //   gradient: "linear-gradient(135deg, #000 0%, #bc4e9c 100%)",
        //   description:
        //     "Browse professional event planners, coordinators, equipment hire, and venue managers."
        // },
        // {
        //   label: "Project Managers",
        //   icon: "mdi-clipboard-check-outline",
        //   slug: "project-managers",
        //   route: "project-managers",
        //   gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
        //   description:
        //     "Find certified project management consultants and operational specialists for corporate or technical needs."
        // }
      ];
    },
  },
  mutations: {
    setIsAdmin(state, payload) {
      state.isa = payload;
      localStorage.setItem("isa", payload);
    },
    setCurrency(state, payload) {
      state.currency = payload;
      localStorage.setItem("currency", payload);
    },
    setTrustAccLabel(state, payload) {
      state.trustAccLabel = payload;
      localStorage.setItem("trustAcc", payload);
    },
    setLogo(state, logoPath) {
      state.logo = logoPath;
      localStorage.setItem("logo", logoPath);
    },
    setStamp(state, stampPath) {
      state.stamp = stampPath;
      localStorage.setItem("stamp", stampPath);
    },
    setAppName(state, appName) {
      state.appName = appName;
      localStorage.setItem("appName", appName);
    },
    setAppMode(state, appMode) {
      state.appMode = appMode;
      localStorage.setItem("o", appMode);
    },
    setScreenWidth(state, screenWidth) {
      state.screenWidth = screenWidth;
    },
    setUpgradeDialog(state, payload) {
      state.upgradeDialog = payload;
    },
    setCurrentUser(state, payload) {
      state.user = payload;
      if (!payload) return;

      //check and set isAdmin for user
      const isa = payload.alevel > 6 ? true : false;
      state.isa = isa;
      localStorage.setItem("isa", isa);

      ["alevel", "password", "password_old", "banned_at"].forEach(
        (k) => delete payload[k]
      );
      localStorage.setItem("currentUser", JSON.stringify(payload));
    },
    setAllCases(state, payload) {
      state.allCases = payload;
      localStorage.setItem("allCases", JSON.stringify(payload));
    },
    setIndustryCategory(state, payload) {
      state.industryCategory = payload;
      localStorage.setItem("industryCategory", JSON.stringify(payload));
    },
    setAllAutoServices(state, payload) {
      state.allAutoServices = payload;
      localStorage.setItem("allAutoServices", JSON.stringify(payload));
    },
    setAllProjects(state, payload) {
      state.allProjects = payload;
      localStorage.setItem("allProjects", JSON.stringify(payload));
    },
    setAllPractices(state, payload) {
      state.allPractices = payload;
      localStorage.setItem("allPractices", JSON.stringify(payload));
    },
    setSelectedDirectoryCompany(state, company) {
      state.selectedDirectoryCompany = company;
      localStorage.setItem("selectedDirectoryCompany", JSON.stringify(company));
    },
    setUserCompany(state, company) {
      state.company = company;
      state.appName = company.name;
      state.currency = company.currency;

      localStorage.setItem("currency", company.currency);
      localStorage.setItem("userCompany", JSON.stringify(company));
      localStorage.setItem("appName", company?.name);
    },
    setGlobalConfirmDelete(state, payload) {
      state.globalConfirmDelete = payload;
    },
    setLoader(state, payload) {
      state.Loader = payload;
    },
    setAuthenticated(state, payload) {
      state.authenticated = payload;
      localStorage.setItem("authenticated", JSON.stringify(payload));
    },
    setAccessToken(state, payload) {
      state.accessToken = payload;
      localStorage.setItem("token", payload);
    },
    setThemeColor(state, color) {
      state.themeColor = color;
      localStorage.setItem("themeColor", color);
    },
    setSecondaryColor(state, color) {
      state.secondaryColor = color;
      localStorage.setItem("secondaryColor", color);
    },
  },
  actions: {
    //not used yet
    async isAuth({ commit }) {
      try {
        const res = await authJs.verifyAuth();

        if (res && res.status === "authenticated" && res.user) {
          commit("setAuthenticated", true);
          commit("setCurrentUser", res.user);
          return true;
        }

        commit("setAuthenticated", false);
        commit("setCurrentUser", null);
        localStorage.removeItem("authenticated");
        localStorage.removeItem("token");
        localStorage.removeItem("currentUser");
        return false;
      } catch (err) {
        commit("setAuthenticated", false);
        commit("setCurrentUser", null);
        localStorage.removeItem("authenticated");
        localStorage.removeItem("token");
        localStorage.removeItem("currentUser");
        console.error("Failed to verifyAuth:", err);
        return false;
      }
    },
    //not used yet
    async isAdmin({ commit }) {
      try {
        const res = await authJs.verifyAdmin();

        if (res && res.status === "is-admin" && res.user) {
          commit("setIsAdmin", true);
          commit("setCurrentUser", res.user);
          return true;
        }

        commit("setIsAdmin", false);
        //commit("setCurrentUser", null);
        //localStorage.removeItem("token");
        return false;
      } catch (err) {
        commit("setIsAdmin", false);
        //commit("setCurrentUser", null);
        //localStorage.removeItem("token");
        console.error("Failed to verifyAuth:", err);
        return false;
      }
    },
    async setCompanyContext({ commit, state, getters }, data = null) {
      let mode = "general";
      let name = "";
      const fresh = data?.fresh || false;
      const slug = data?.slug || null;
      const emis = data?.emis || null;
      const host = window.location.hostname.toLowerCase();
      let targetCompany = null;

      if (fresh) {
        try {
          if (slug) {
            targetCompany = await companiesJs.getCompanyBySlug(slug);
          } else if (emis) {
            targetCompany = await companiesJs.getCompanyWithEmis(emis);
          } else {
            targetCompany = await companiesJs.getCompanyWithDomain(host);
          }
        } catch (err) {
          console.error("Failed to resolve fresh company:", err);
        }
      } else {
        const company = data?.company || null;
        targetCompany = company || state.company || null;

        // If no company exists in state/params, attempt async fetch from backend matching host domain
        if (!targetCompany) {
          try {
            if (slug) {
              targetCompany = await companiesJs.getCompanyBySlug(slug);
            } else if (emis) {
              targetCompany = await companiesJs.getCompanyWithEmis(emis);
            } else {
              targetCompany = await companiesJs.getCompanyWithDomain(host);
            }
          } catch (err) {
            console.error("Failed to resolve company:", err);
          }
        }
      }

      // 2. Evaluate Mode & Name from Target Company or Host Domain Fallback
      if (targetCompany) {
        const industry = targetCompany.industry?.toLowerCase() || "";

        const isLegal = industry.includes("legal");
        const isAutoService = industry.includes("auto");
        const isProjects = state.projectIndustries?.includes(
          targetCompany.industry
        );
        const isEvents = state.eventIndustries?.includes(
          targetCompany.industry
        );
        const isPractice = state.privatePracticeIndustries?.includes(
          targetCompany.industry
        );
        const isSocial = state.socialWorkerIndustries?.includes(
          targetCompany.industry
        );

        if (isLegal) mode = "legal";
        else if (isAutoService) mode = "autoservice";
        else if (isProjects) mode = "projects";
        else if (isEvents) mode = "events";
        else if (isPractice) mode = "privatePractice";
        else if (isSocial) mode = "social";
        else mode = "general";

        name = targetCompany.company?.name || targetCompany.name || name;
        commit("setUserCompany", targetCompany);
        commit("setThemeColor", targetCompany?.site_colors?.primary_bg_color);
        commit(
          "setSecondaryColor",
          targetCompany?.site_colors?.secondary_bg_color
        );
      } else if (host.includes("legalcasebilling")) {
        name = "Legal Case Billing";
        mode = "legal";
      } else if (host.includes("bitsinvoice")) {
        name = "BitsInvoice";
        mode = "general";
      } else if (host.includes("172.20") || host.includes("localhost")) {
        name = "BitsInvoice Env";
        mode = "general";
      }

      // 3. Commit Computed State Updates
      const trustLabel = state.trustAccMap ? state.trustAccMap[mode] : "";
      commit("setTrustAccLabel", trustLabel);
      commit("setAppMode", mode);
      commit("setAppName", name);

      localStorage.setItem("o", mode);
      localStorage.setItem("appName", name);

      return { mode, name, targetCompany };
    },
    async getAllServiceCards({ commit, state }, data = null) {
      const isFresh = typeof data === "boolean" ? data : Boolean(data?.fresh);

      if (!isFresh && state.allAutoServices?.length > 0) {
        return state.allAutoServices;
      }

      try {
        const jobCards = await autoServicesJs.getAllAutoJobCards();
        commit("setAllAutoServices", jobCards);
        return jobCards;
      } catch (error) {
        console.error("Failed to fetch service cards:", error);
      }

      // Fallback return if the API request fails or returns an unsuccessful status
      return state.allAutoServices || [];
    },
    async getAutoServiceByInvoice({ commit, state }, data = null) {
      const invoiceNumber =
        typeof data === "string" || typeof data === "number"
          ? String(data)
          : data?.invoiceNumber;
      if (!invoiceNumber) {
        return null;
      }
      //const isFresh = typeof data === 'boolean' ? data : Boolean(data?.fresh);

      // if (!isFresh && state.allAutoServices?.length > 0) {
      //   return state.allAutoServices;
      // }

      try {
        const jobCard = await autoServicesJs.getAutoServiceByInvoice(
          invoiceNumber
        );
        //commit("setAllAutoServices", jobCards);
        return jobCard;
      } catch (error) {
        console.error("Failed to fetch service card:", error);
      }
    },
    async getGenInvoiceByInvoiceNumber({ commit, state }, data = null) {
      const invoiceNumber =
        typeof data === "string" || typeof data === "number"
          ? String(data)
          : data?.invoiceNumber;
      if (!invoiceNumber) {
        return null;
      }
      //const isFresh = typeof data === 'boolean' ? data : Boolean(data?.fresh);

      // if (!isFresh && state.allAutoServices?.length > 0) {
      //   return state.allAutoServices;
      // }

      try {
        const invoice = await invoicesAndQuotesJs.getInvoiceByInvoiceNumber(
          invoiceNumber
        );
        //commit("setAllAutoServices", invoices);
        return invoice;
      } catch (error) {
        console.error("Failed to fetch service card:", error);
      }
    },
    async getLegalCaseByInvoiceNumber({ commit, state }, data = null) {
      const invoiceNumber =
        typeof data === "string" || typeof data === "number"
          ? String(data)
          : data?.invoiceNumber;
      if (!invoiceNumber) {
        return null;
      }
      //const isFresh = typeof data === 'boolean' ? data : Boolean(data?.fresh);

      // if (!isFresh && state.allAutoServices?.length > 0) {
      //   return state.allAutoServices;
      // }

      try {
        const legalCase = await legalCasesJs.getLegalCaseByInvoiceNumber(
          invoiceNumber
        );
        //commit("setAllAutoServices", invoices);
        return legalCase;
      } catch (error) {
        console.error("Failed to fetch service card:", error);
      }
    },
    async getPracticeByInvoiceNumber({ commit, state }, data = null) {
      const invoiceNumber =
        typeof data === "string" || typeof data === "number"
          ? String(data)
          : data?.invoiceNumber;
      const table = data?.table || "social_work_files";
      if (!invoiceNumber || !table) {
        return null;
      }
      //const isFresh = typeof data === 'boolean' ? data : Boolean(data?.fresh);

      // if (!isFresh && state.allAutoServices?.length > 0) {
      //   return state.allAutoServices;
      // }

      try {
        const practice = await practicesJs.getPracticeByInvoiceNumber(
          table,
          invoiceNumber
        );
        //commit("setAllAutoServices", invoices);
        return practice;
      } catch (error) {
        console.error("Failed to fetch service card:", error);
      }
    },
    async getProjectByInvoiceNumber({ commit, state }, data = null) {
      const invoiceNumber =
        typeof data === "string" || typeof data === "number"
          ? String(data)
          : data?.invoiceNumber;

      if (!invoiceNumber) {
        return null;
      }
      //const isFresh = typeof data === 'boolean' ? data : Boolean(data?.fresh);

      // if (!isFresh && state.allAutoServices?.length > 0) {
      //   return state.allAutoServices;
      // }

      try {
        const project = await projectsJs.getProjectByInvoiceNumber(
          invoiceNumber
        );
        //commit("setAllAutoServices", invoices);
        return project;
      } catch (error) {
        console.error("Failed to fetch service card:", error);
      }
    },
  },
  modules: {},
});
